import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { AttachmentsSection } from "./AttachmentsSection";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "../lib/utils";
import { revenireApi, attachmentsApi } from "../services/api";

/**
 * Card pentru o revenire post-consultație. Include:
 *  - textarea salvat automat la blur (echivalent cu admin_notes)
 *  - AttachmentsSection filtrat pe revenire_id pentru poze/fișiere
 *  - buton delete (cu confirmare) — șterge revenirea + toate fișierele ei
 */
export function RevenireCard({
  patientId,
  revenire,
  attachments,
  index,
  onTextUpdated,
  onDeleted,
  onAttachmentAdded,
  onAttachmentRemoved,
}) {
  const [text, setText] = useState(revenire.text || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleBlur = async () => {
    if ((revenire.text || "") === text) return;
    setSaving(true);
    try {
      const updated = await revenireApi.update(patientId, revenire.id, text);
      if (onTextUpdated) onTextUpdated(revenire.id, updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvarea revenirii");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await revenireApi.delete(patientId, revenire.id);
      toast.success("Revenirea a fost ștearsă");
      if (onDeleted) onDeleted(revenire.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la ștergere");
    } finally {
      setDeleting(false);
    }
  };

  const handleUploadForRevenire = async (file, onProgress) => {
    const att = await attachmentsApi.adminUpload(patientId, file, onProgress, revenire.id);
    if (onAttachmentAdded) onAttachmentAdded(att);
    return att;
  };

  const handleAttachmentDelete = async (fileId) => {
    await attachmentsApi.adminDelete(patientId, fileId);
    if (onAttachmentRemoved) onAttachmentRemoved(fileId);
  };

  const handleAttachmentView = async (att) => {
    const url = attachmentsApi.adminViewUrl(patientId, att.id);
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error("Nu s-a putut deschide fișierul.");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const w = window.open(objectUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    if (!w) toast.warning("Popup-ul a fost blocat. Verifică setările browser-ului.");
  };

  return (
    <Card data-testid={`revenire-card-${revenire.id}`}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            Revenire #{index + 1}
            {saving && (
              <span className="text-xs font-normal text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Se salvează…
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Creat: {formatDateTime(revenire.created_at)}
            {revenire.updated_at && revenire.updated_at !== revenire.created_at && (
              <> • Actualizat: {formatDateTime(revenire.updated_at)}</>
            )}
          </p>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              disabled={deleting}
              data-testid={`revenire-delete-${revenire.id}`}
              title="Șterge revenirea și fișierele atașate"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Ștergi revenirea?</AlertDialogTitle>
              <AlertDialogDescription>
                Această acțiune va șterge nota și toate fișierele/pozele atașate acestei reveniri. Nu poate fi anulată.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anulează</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive hover:bg-destructive/90"
                data-testid={`revenire-delete-confirm-${revenire.id}`}
              >
                Șterge
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardHeader>
      <CardContent className="space-y-4">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={handleBlur}
          placeholder="Notează observațiile privind revenirea sportivului (evoluție, tratament, recomandări...). Se salvează automat."
          className="min-h-[110px] text-sm"
          data-testid={`revenire-textarea-${revenire.id}`}
        />
        <AttachmentsSection
          title="Poze / fișiere atașate revenirii"
          description="Adaugă poze cu evoluția, analize, capturi din documente. Acceptate: JPG, PNG, HEIC, PDF."
          attachments={attachments}
          onUpload={handleUploadForRevenire}
          onDelete={handleAttachmentDelete}
          onView={handleAttachmentView}
          canUpload={true}
          canDelete={true}
          emptyText="Nicio poză/fișier atașat revenirii."
          testIdPrefix={`revenire-files-${revenire.id}`}
        />
      </CardContent>
    </Card>
  );
}
